import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronDown } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

export interface Option {
  label: string;
  value: string;
}

interface MunDropdownMultipleProps {
  multiple?: true;
  options: Option[];
  value: Option[];
  onChange: (value: Option[]) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  onClose?: () => void;
  showSelectAll?: boolean;
  avoidCollisions?: boolean;
}

interface MunDropdownSingleProps {
  multiple: false;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  onClose?: () => void;
  showSelectAll?: never;
  avoidCollisions?: boolean;
}

type MunDropdownProps = MunDropdownMultipleProps | MunDropdownSingleProps;

export function MunDropdown(props: MunDropdownMultipleProps): JSX.Element;
export function MunDropdown(props: MunDropdownSingleProps): JSX.Element;
export function MunDropdown({
  options,
  value,
  onChange,
  placeholder = "Select...",
  label,
  className,
  disabled = false,
  onClose,
  multiple = true,
  showSelectAll = true,
  avoidCollisions,
}: MunDropdownProps) {
  const [open, setOpen] = useState(false);

  const isSelected = useCallback(
    (val: string) => {
      if (multiple) {
        return (value as Option[]).some((v) => v.value === val);
      }
      return (value as string) === val;
    },
    [value, multiple]
  );

  const handleSelect = useCallback(
    (currentValue: string) => {
      if (multiple) {
        const option = options.find((opt) => opt.value === currentValue);
        if (!option) return;

        const currentValues = value as Option[];
        if (isSelected(currentValue)) {
          (onChange as (value: Option[]) => void)(
            currentValues.filter((v) => v.value !== currentValue)
          );
        } else {
          (onChange as (value: Option[]) => void)([...currentValues, option]);
        }
      } else {
        (onChange as (value: string) => void)(
          (value as string) === currentValue ? "" : currentValue
        );
        setOpen(false);
      }
    },
    [isSelected, onChange, value, options, multiple]
  );

  const handleSelectAll = useCallback(() => {
    if (!multiple) return;
    const currentValues = value as Option[];
    if (currentValues.length === options.length) {
      (onChange as (value: Option[]) => void)([]);
      return;
    }
    (onChange as (value: Option[]) => void)(options);
  }, [value, options, onChange, multiple]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (!newOpen && onClose) {
        onClose();
      }
    },
    [onClose]
  );

  const displayText = useMemo(() => {
    if (multiple) {
      const currentValues = value as Option[];
      if (currentValues.length === 0) {
        return placeholder;
      }
      if (currentValues.length === 1) {
        return currentValues[0].label;
      }
      return `${currentValues.length} ${label?.toLowerCase() || "selected"}`;
    }
    const selectedOption = options.find(
      (opt) => opt.value === (value as string)
    );
    return selectedOption?.label || placeholder;
  }, [value, options, placeholder, label, multiple]);

  const sortedOptions = useMemo(() => {
    if (!multiple) return options;
    const currentValues = value as Option[];
    const selectedValues = new Set(currentValues.map((v) => v.value));
    const selected = options.filter((opt) => selectedValues.has(opt.value));
    const unselected = options.filter((opt) => !selectedValues.has(opt.value));
    return [...selected, ...unselected];
  }, [options, value, multiple]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-expanded={open}
          disabled={disabled}
          className={cn("min-w-[140px] justify-between", className)}
        >
          <span className="truncate">{displayText}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start" avoidCollisions={avoidCollisions}>
        <Command>
          <CommandInput
            placeholder={`Search ${label?.toLowerCase() || "options"}...`}
          />
          <CommandList>
            <CommandEmpty>
              No {label?.toLowerCase() || "options"} found.
            </CommandEmpty>
            <CommandGroup>
              {multiple && showSelectAll && (
                <CommandItem
                  onSelect={handleSelectAll}
                  className="flex items-center gap-2 font-medium"
                >
                  <Check
                    className={cn(
                      "h-4 w-4",
                      (value as Option[]).length === options.length
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  Select All
                </CommandItem>
              )}
              {sortedOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  onSelect={() => handleSelect(option.value)}
                  className={cn(
                    "flex items-center gap-2",
                    isSelected(option.value) && "bg-accent"
                  )}
                >
                  <Check
                    className={cn(
                      "h-4 w-4",
                      isSelected(option.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
